import { useState } from 'react';

import type { useToast } from '../../../context/ToastContext';
import type { Language } from '../../../i18n';
import { trackAuthEvent } from '../../../services/analytics';
import { userProfileAPI } from '../../../services/api';
import { persistProfileLanguage } from '../profileLanguage';
import type { Translate } from '../profileTypes';

interface UseLanguagePreferenceOptions {
  changeLanguage: (language: Language) => Promise<void>;
  showToast: ReturnType<typeof useToast>['showToast'];
  t: Translate;
}

/** Language-select modal state and the language switch. */
export function useLanguagePreference({ changeLanguage, showToast, t }: UseLanguagePreferenceOptions) {
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);

  const openLanguageSelect = () => {
    trackAuthEvent('language_modal_opened');
    setShowLanguageSelect(true);
  };

  const handleSelectLanguage = async (code: Language) => {
    await changeLanguage(code);
    // The profile switch is the one place that persists the
    // language server-side for emails/notifications (D22).
    void persistProfileLanguage(code, userProfileAPI.updateProfile);
    setShowLanguageSelect(false);
    trackAuthEvent('language_changed', { language: code });
    showToast(t('profile.languageChanged'), 'success');
  };

  return {
    showLanguageSelect,
    openLanguageSelect,
    closeLanguageSelect: () => setShowLanguageSelect(false),
    handleSelectLanguage,
  };
}
